import React, { FC } from 'react';
import { ScrollView, Text, View } from 'react-native';
import i18n from 'i18n-js';
import ColorWrapper, { WithColor } from '@styles/colors';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import ToolIcon from '@v2/components/imageHandling/components/DrawingCanvas/components/ToolIcon';
import { ANNOTATION_COLORS, ANNOTATION_STROKE_WIDTHS, ERASER_TOOL, TOOL_LABELS, TOOL_ORDER, TOOL_TEST_IDS } from '@v2/components/imageHandling/components/DrawingCanvas/constants';
import { ActiveTool } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import { useManageDrawingBottomToolbar } from './hooks/useManageDrawingBottomToolbar';

interface DrawingBottomToolbarProps extends WithColor {
  activeTool: ActiveTool;
  strokeColor: string;
  strokeWidth: number;
  fillActive: boolean;
  canUndo: boolean;
  canRedo: boolean;
  showFillToggle: boolean;
  onPickTool: (tool: ActiveTool) => void;
  onPickColor: (color: string) => void;
  onPickWidth: (width: number) => void;
  onToggleFill: (filled: boolean) => void;
  onUndo: () => void;
  onRedo: () => void;
}

const DrawingBottomToolbar: FC<DrawingBottomToolbarProps> = ({
  C,
  activeTool,
  strokeColor,
  strokeWidth,
  fillActive,
  canUndo,
  canRedo,
  showFillToggle,
  onPickTool,
  onPickColor,
  onPickWidth,
  onToggleFill,
  onUndo,
  onRedo,
}) => {
  const isEraser = activeTool === ERASER_TOOL;
  const { dims } = useManageDrawingBottomToolbar();

  return (
    <View
      testID={TEST_IDS.drawingCanvas.bottomToolbar}
      style={{
        backgroundColor: C('Background/Light/Panel [web]'),
        borderTopWidth: 1,
        borderTopColor: C('Content/Light/Tertiary'),
        paddingVertical: scale(8),
      }}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          flexGrow: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: dims.rowGap,
          paddingHorizontal: scale(8),
        }}
      >
        {TOOL_ORDER.map((tool) => {
          const active = activeTool === tool;
          return (
            <TouchableElement key={tool} testID={TOOL_TEST_IDS[tool]} onPress={() => onPickTool(tool)}>
              {({ boxStyle }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      minWidth: dims.toolMin,
                      minHeight: dims.toolMin,
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingHorizontal: dims.toolPaddingX,
                      paddingVertical: dims.toolPaddingY,
                      gap: scale(2),
                      borderRadius: dims.toolBorderRadius,
                      backgroundColor: active ? C('Button/Light/Secondary') : 'transparent',
                    },
                  ]}
                >
                  <ToolIcon name={tool} color={active ? C('Selected/Light/Primary') : C('Label/Light/Tertiary')} size={dims.toolIcon} />
                  {/* Invisible bold ghost reserves the wider width — overlaid visible label flips fonts without layout jump. */}
                  <View>
                    <Text numberOfLines={1} style={[font['Button 3'], { opacity: 0 }]}>
                      {i18n.t(TOOL_LABELS[tool])}
                    </Text>
                    <Text
                      numberOfLines={1}
                      style={[
                        active ? font['Button 3'] : font['Caption 2'],
                        {
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          textAlign: 'center',
                          color: active ? C('Selected/Light/Primary') : C('Label/Light/Tertiary'),
                        },
                      ]}
                    >
                      {i18n.t(TOOL_LABELS[tool])}
                    </Text>
                  </View>
                </View>
              )}
            </TouchableElement>
          );
        })}

        <View style={{ width: 1, height: dims.dividerH1, backgroundColor: C('Content/Light/Tertiary'), marginHorizontal: dims.dividerMarginH1 }} />

        <TouchableElement testID={TEST_IDS.drawingCanvas.undoButton} onPress={onUndo} disabled={!canUndo}>
          {({ boxStyle }) => (
            <View
              style={[
                boxStyle,
                { minWidth: dims.toolMin, minHeight: dims.toolMin, alignItems: 'center', justifyContent: 'center', opacity: canUndo ? 1 : 0.3 },
              ]}
            >
              <ToolIcon name="undo" color={C('Label/Light/Secondary')} size={dims.undoRedoIcon} />
            </View>
          )}
        </TouchableElement>
        <TouchableElement testID={TEST_IDS.drawingCanvas.redoButton} onPress={onRedo} disabled={!canRedo}>
          {({ boxStyle }) => (
            <View
              style={[
                boxStyle,
                { minWidth: dims.toolMin, minHeight: dims.toolMin, alignItems: 'center', justifyContent: 'center', opacity: canRedo ? 1 : 0.3 },
              ]}
            >
              <ToolIcon name="redo" color={C('Label/Light/Secondary')} size={dims.undoRedoIcon} />
            </View>
          )}
        </TouchableElement>
      </ScrollView>

      {/* Eraser keeps the row mounted but disabled so layout doesn't jump. */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          flexGrow: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: dims.rowGap,
          paddingTop: dims.secondRowPaddingTop,
          paddingHorizontal: dims.secondRowPaddingX,
          opacity: isEraser ? 0.4 : 1,
        }}
        scrollEnabled={!isEraser}
        pointerEvents={isEraser ? 'none' : 'auto'}
      >
        {ANNOTATION_COLORS.map((colorConfig) => {
          const isActive = strokeColor === colorConfig.value;
          const borderColor = isActive
            ? C('Button/Light/Primary')
            : colorConfig.inactiveBorder
            ? C(colorConfig.inactiveBorder)
            : 'transparent';
          return (
            <TouchableElement key={colorConfig.value} testID={TEST_IDS.drawingCanvas.colorSwatch} onPress={() => onPickColor(colorConfig.value)}>
              {({ boxStyle }) => (
                <View style={[boxStyle, { minWidth: dims.colorContainerMin, minHeight: dims.colorContainerMin, alignItems: 'center', justifyContent: 'center' }]}>
                  <View
                    style={{
                      width: dims.colorCircle,
                      height: dims.colorCircle,
                      borderRadius: dims.colorCircle / 2,
                      backgroundColor: colorConfig.value,
                      borderWidth: dims.colorActiveBorder,
                      borderColor,
                    }}
                  />
                </View>
              )}
            </TouchableElement>
          );
        })}

        <View style={{ width: 1, height: dims.dividerH2, backgroundColor: C('Content/Light/Tertiary'), marginHorizontal: dims.dividerMarginH2 }} />

        {ANNOTATION_STROKE_WIDTHS.map((w) => (
          <TouchableElement key={w} testID={TEST_IDS.drawingCanvas.widthSwatch} onPress={() => onPickWidth(w)}>
            {({ boxStyle }) => (
              <View
                style={[
                  boxStyle,
                  {
                    minWidth: dims.widthMin,
                    minHeight: dims.widthHeight,
                    borderRadius: dims.widthBorderRadius,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: strokeWidth === w ? C('Button/Light/Secondary') : 'transparent',
                  },
                ]}
              >
                <View
                  style={{
                    width: Math.min(w * 3, scale(20)),
                    height: Math.max(w, 1.5),
                    borderRadius: w / 2,
                    backgroundColor: strokeWidth === w ? C('Label/Light/Primary') : C('Label/Light/Tertiary'),
                  }}
                />
              </View>
            )}
          </TouchableElement>
        ))}

        {showFillToggle ? (
          <>
            <View style={{ width: 1, height: dims.dividerH2, backgroundColor: C('Content/Light/Tertiary'), marginHorizontal: dims.dividerMarginH2 }} />
            <TouchableElement testID={TEST_IDS.drawingCanvas.fillOutline} onPress={() => onToggleFill(false)}>
              {({ boxStyle }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      paddingHorizontal: dims.fillPaddingX,
                      paddingVertical: dims.fillPaddingY,
                      borderRadius: dims.fillBorderRadius,
                      backgroundColor: !fillActive ? C('Button/Light/Secondary') : 'transparent',
                    },
                  ]}
                >
                  <Text style={[font.Body, { color: !fillActive ? C('Label/Light/Primary') : C('Label/Light/Tertiary') }]}>{i18n.t('Outline')}</Text>
                </View>
              )}
            </TouchableElement>
            <TouchableElement testID={TEST_IDS.drawingCanvas.fillFilled} onPress={() => onToggleFill(true)}>
              {({ boxStyle }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      paddingHorizontal: dims.fillPaddingX,
                      paddingVertical: dims.fillPaddingY,
                      borderRadius: dims.fillBorderRadius,
                      backgroundColor: fillActive ? C('Button/Light/Secondary') : 'transparent',
                    },
                  ]}
                >
                  <Text style={[font.Body, { color: fillActive ? C('Label/Light/Primary') : C('Label/Light/Tertiary') }]}>{i18n.t('Filled')}</Text>
                </View>
              )}
            </TouchableElement>
          </>
        ) : null}
      </ScrollView>
    </View>
  );
};

export default ColorWrapper(DrawingBottomToolbar);
